import { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";

import AxiosFactory from "../../services/http.service";
import ApiRoutes from "../../config/api.routes";
import { getReadOnlyContract } from "../../services/contract.service";
import { isUserRejection } from "../../services/relay.service";
import {
  ChallengeKind,
  ChallengeRecord,
  ChallengeState,
  describeChainError,
  findOpenChallenge,
  readChallenge,
  redrawPanel,
  seatPanel,
  verdictSpec,
} from "../../services/challenge.service";
import usePanelVerdict from "../../hooks/usePanelVerdict";
import useVerdictSignatures from "../../hooks/useVerdictSignatures";
import PanelSignatureCollector from "./PanelSignatureCollector";
import VerdictSignatureTally from "./VerdictSignatureTally";
import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import PopupDialog from "../PopupDialog";
import {
  CalloutText,
  DetailBody,
  DetailCard,
  DetailTitle,
  DialogSheet,
  ErrorNote,
  Footnote,
  InfoCallout,
  MetaItem,
  MonoMeta,
  SectionLabel,
  SegmentedChoice,
  figmaButtonSx,
} from "../Settlement/settlementStyles";

// The V10.1 determination dialog — EcoChallenge.recordOutcome(challengeId, upheld, inBreach,
// signatures). One dialog for both stages a minted covenant can be challenged at; the wrappers
// JudgeChallengeDialog (deed) and JudgeTermChallengeDialog (verification) pass the stage and
// keep their V9 props so the panels that mount them did not have to change.
//
// What replaced what:
//   V9.2  EcoBank.judgeDeedChallenge(tokenId, upheld)  — one GTA, one relayed signature
//   V9.2  EcoBank.judgeTermFraud(tokenId, upheld)      — one GTA, inverted polarity
//   V10.1 EcoChallenge.recordOutcome(...)              — a drawn panel (or one checker), signing
//                                                        EIP-712 typed data; anyone submits
//
// Two independent booleans, no score (see challenge.service.ts). Neither is "the destructive
// one" any more, and neither refunds anybody:
//   upheld     false → the verification stands; the CHALLENGER is flagged (a failed challenge is
//                      not free). A held deed settlement is released immediately.
//              true  → the VERIFIER is flagged, and the standing attestor with them. FIELD: the
//                      attest slot is forfeited and the seat moves to the challenger, who
//                      re-verifies; EVIDENCE: no seat moves, a better record must be filed and
//                      releases halt until it is. A held deed settlement STAYS held until the
//                      covenant is reseated and re-verified — it is released then, not refunded.
//   inBreach   true  → the TREE drip freezes until a real re-verification; back-pay resumes on
//                      cure. Independent of upheld: a verifier can be right about breached land.
//
// The panel is drawn by seatPanel once the response window closes. A backend job is meant to do
// that; the dialog offers it as a manual fallback (like SettlementPanel's finalise button) so the
// flow can be worked without the worker. The same goes for the §6 redraw: a seated panel that has
// not reached two signatures within panelWindow may be redrawn exactly once (redrawPanel), after
// which expireChallenge — a backend job, not offered here — ends a stalled challenge.
//
// Collecting the signatures (V10.1.2): each seat signs here and the signature goes to the
// backend (useVerdictSignatures / VerdictSignatureTally), which submits recordOutcome from its
// own wallet once one verdict has enough. The tally is polled, so every seat sees it fill and
// the dialog notices when the determination lands. Only when that endpoint cannot be reached
// does the dialog fall back to the old lane — signatures pasted between seats and recordOutcome
// sent from this wallet (usePanelVerdict / PanelSignatureCollector).

export type ChallengeStage = "deed" | "verification";

type UpheldChoice = "dismiss" | "uphold";
type BreachChoice = "no" | "yes";

const UPHELD_OPTIONS: { value: UpheldChoice; label: string }[] = [
  { value: "dismiss", label: "Dismiss" },
  { value: "uphold", label: "Uphold" },
];
const BREACH_OPTIONS: { value: BreachChoice; label: string }[] = [
  { value: "no", label: "Not in breach" },
  { value: "yes", label: "In breach" },
];

export interface RecordOutcomeDialogProps {
  open: boolean;
  onClose: () => void;
  stage: ChallengeStage;
  // On-chain token id (EcoChallenge's subject / the Bank's settlement key).
  tokenId: number | string;
  // Connected wallet, so the collector can tell whether it holds a seat.
  viewerAddress?: string | null;
  // Called once the determination is mined, so the caller can re-read.
  onJudged?: (upheld: boolean, inBreach: boolean) => void;
}

interface EvidenceRecord {
  evidence: string | null;
  at: Date | null;
}

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const KIND_LABEL: Record<number, string> = {
  [ChallengeKind.FIELD]: "Field challenge — the challenger has been to the land",
  [ChallengeKind.EVIDENCE]: "Evidence challenge — a desk review of the record",
};

const STATE_LABEL: Record<number, string> = {
  [ChallengeState.OPEN]: "Open — awaiting the verifier's response",
  [ChallengeState.RESPONDED]: "Responded — panel not yet drawn",
  [ChallengeState.SEATED]: "Panel seated — awaiting the determination",
  [ChallengeState.DETERMINED]: "Determined",
  [ChallengeState.DISMISSED_BY_LAPSE]: "Dismissed by lapse",
};

const RecordOutcomeDialog = ({ open, onClose, stage, tokenId, viewerAddress, onJudged }: RecordOutcomeDialogProps) => {
  const [challenge, setChallenge] = useState<ChallengeRecord | null>(null);
  const [evidence, setEvidence] = useState<EvidenceRecord | null>(null);
  const [responseWindowSec, setResponseWindowSec] = useState<number | null>(null);
  const [panelWindowSec, setPanelWindowSec] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [upheldChoice, setUpheldChoice] = useState<UpheldChoice | null>(null);
  const [breachChoice, setBreachChoice] = useState<BreachChoice | null>(null);
  const [seating, setSeating] = useState(false);
  const [seatError, setSeatError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setChallenge(null);
    setEvidence(null);
    try {
      const contract = getReadOnlyContract("challenge");
      const [record, responseWindow, panelWindow] = await Promise.all([
        findOpenChallenge(tokenId),
        contract ? contract.responseWindow().then((v: bigint) => Number(v)) : Promise.resolve(null),
        contract ? contract.panelWindow().then((v: bigint) => Number(v)) : Promise.resolve(null),
      ]);
      setChallenge(record);
      setResponseWindowSec(responseWindow);
      setPanelWindowSec(panelWindow);
      if (record) {
        // The evidence is not in the struct — only in ChallengeRaised (and, for a deed challenge,
        // in the DEED_CHALLENGED audit row the backend still writes off EcoBank's DeedChallenged).
        // Try the audit row first, then the event; neither is load-bearing for the verdict.
        let found: EvidenceRecord | null = null;
        try {
          const axios = new AxiosFactory().axios;
          const rows = await axios.get(ApiRoutes.settlement.history(tokenId)).then((res) => res.data);
          const candidates = (Array.isArray(rows) ? rows : []).filter(
            (row: any) => row?.type === "deed_challenged" || row?.type === "term_challenged"
          );
          const latest = candidates[candidates.length - 1];
          if (latest?.data?.reason) {
            found = { evidence: latest.data.reason, at: latest.createdAt ? new Date(latest.createdAt) : null };
          }
        } catch {
          // fall through to the chain
        }
        if (!found && contract) {
          try {
            const logs = await contract.queryFilter(contract.filters.ChallengeRaised(record.challengeId));
            const log: any = logs[logs.length - 1];
            if (log?.args) {
              found = { evidence: String(log.args.evidence ?? ""), at: record.openedAt };
            }
          } catch {
            // an RPC with a capped log range — the verdict does not depend on it
          }
        }
        setEvidence(found ?? { evidence: null, at: record.openedAt });
      }
    } catch (e: any) {
      setLoadError(describeChainError(e));
    } finally {
      setLoading(false);
    }
  }, [tokenId]);

  useEffect(() => {
    if (open) {
      setUpheldChoice(null);
      setBreachChoice(null);
      setSeatError(null);
      load();
    }
  }, [open, load]);

  const upheld = upheldChoice === "uphold";
  const inBreach = breachChoice === "yes";
  const verdictChosen = upheldChoice !== null && breachChoice !== null;
  const seated = challenge?.state === ChallengeState.SEATED;

  // The server-collected lane, polled while the dialog is open on a seated challenge.
  const serverLane = useVerdictSignatures(seated && challenge ? challenge.challengeId : null, open && seated);
  const fallback = serverLane.unavailable;

  // A new builder identity whenever the verdict changes — the hook clears its signatures on it.
  // Only built on the fallback lane: while the server collects, nothing is gathered in the browser.
  const build = useMemo(() => {
    if (!challenge || !seated || !verdictChosen || !fallback) {
      return null;
    }
    const id = challenge.challengeId;
    return () => verdictSpec(id, upheld, inBreach);
  }, [challenge, seated, verdictChosen, upheld, inBreach, fallback]);

  const verdict = usePanelVerdict(build, viewerAddress);

  useEffect(() => {
    if (verdict.status === "success") {
      onJudged?.(upheld, inBreach);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdict.status]);

  // The server's recordOutcome landed: its read says the challenge is no longer SEATED. Re-read it
  // by id (findOpenChallenge only returns LIVE challenges, so a determined one would read as
  // "none found") and report the verdict the chain actually recorded — not the one picked here,
  // which may differ if another pair reached the threshold first.
  const serverSeated = serverLane.status?.seated;
  const challengeKey = challenge?.challengeId;
  useEffect(() => {
    if (!open || serverSeated !== false || !seated || challengeKey === undefined) {
      return;
    }
    let cancelled = false;
    readChallenge(challengeKey)
      .then((record) => {
        if (cancelled) {
          return;
        }
        setChallenge(record);
        if (record.state === ChallengeState.DETERMINED) {
          onJudged?.(record.upheld, record.inBreach);
        }
      })
      .catch(() => {
        // the next poll tries again
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serverSeated, seated, challengeKey]);

  const handleClose = () => {
    if (verdict.isPending || seating || serverLane.signing) {
      return;
    }
    onClose();
  };

  const drawPanel = async (redraw = false) => {
    if (!challenge) {
      return;
    }
    setSeating(true);
    setSeatError(null);
    try {
      await (redraw ? redrawPanel(challenge.challengeId) : seatPanel(challenge.challengeId));
      await load();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setSeatError(describeChainError(e));
      }
    } finally {
      setSeating(false);
    }
  };

  // §6: the panel's own window. Once it lapses with no determination the panel may be redrawn
  // once; the seats it drew are then no longer the ones the contract will count.
  const panelCloses =
    seated && challenge?.seatedAt && panelWindowSec !== null
      ? new Date(challenge.seatedAt.getTime() + panelWindowSec * 1000)
      : null;
  const panelLapsed = !!panelCloses && panelCloses <= new Date();
  const canRedraw = seated && panelLapsed && !!challenge && !challenge.redrawn;

  const meta: MetaItem[] = [];
  if (challenge) {
    meta.push({ label: "Raised by", value: shortAddress(challenge.challenger) });
    meta.push({ label: "Against", value: shortAddress(challenge.verifier) });
    if (evidence?.at) {
      meta.push({ label: "", value: evidence.at.toLocaleString(), muted: true });
    }
  }

  const responseCloses =
    challenge?.openedAt && responseWindowSec !== null
      ? new Date(challenge.openedAt.getTime() + responseWindowSec * 1000)
      : null;
  const responseStillOpen = challenge?.state === ChallengeState.OPEN && !!responseCloses && responseCloses > new Date();

  const intro =
    stage === "deed"
      ? "The settlement is held while this challenge stands. Dismissing releases it and flags the challenger. " +
        "Upholding keeps it held until the covenant is reseated and re-verified — this verdict never refunds the patron."
      : "Dismissing leaves the verification standing and flags the challenger. Upholding flags the verifier: on a " +
        "field challenge the seat moves to the challenger, who re-verifies the land; on an evidence challenge a " +
        "better record must be filed and releases halt until it is.";

  return (
    <PopupDialog open={open} onClose={handleClose} surface="sheet">
      <DialogSheet>
        <InfoCallout surface="white">
          <CalloutText>{intro}</CalloutText>
        </InfoCallout>

        <DetailCard>
          <DetailTitle>Challenge under review</DetailTitle>
          {loading ? (
            <CircularProgress size={18} sx={{ color: "#000" }} />
          ) : challenge ? (
            <>
              <DetailBody>{KIND_LABEL[challenge.kind] ?? "Challenge"}</DetailBody>
              <DetailBody>{evidence?.evidence || "No evidence text could be loaded for this challenge."}</DetailBody>
              <MonoMeta items={meta} muted="#999" stacked />
              <DetailBody style={{ marginTop: "8px", color: "#666" }}>
                Status: {STATE_LABEL[challenge.state] ?? "Unknown"}
              </DetailBody>
            </>
          ) : (
            <DetailBody>{loadError ?? "No open challenge was found on this token."}</DetailBody>
          )}
        </DetailCard>

        {/* Not seated yet: the response window, and the manual draw. */}
        {challenge && !seated && !loading && challenge.state !== ChallengeState.DETERMINED && (
          <>
            <InfoCallout surface="white" tone="warning">
              <CalloutText>
                {responseStillOpen
                  ? `The challenged verifier can respond until ${responseCloses?.toLocaleString()}. The panel is drawn after that.`
                  : "The panel has not been drawn yet. A backend job draws it; you can also draw it now."}
              </CalloutText>
            </InfoCallout>
            {seatError && <ErrorNote>{seatError}</ErrorNote>}
            {seating ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
                Drawing the panel…
              </PopupProcessingButton>
            ) : (
              <PopupActionButton
                onClick={() => drawPanel(false)}
                disabled={responseStillOpen}
                variant="contained"
                fullWidth
                sx={figmaButtonSx}
              >
                Draw the panel — pays gas
              </PopupActionButton>
            )}
            <PopupGhostButton onClick={handleClose} fullWidth>
              Close
            </PopupGhostButton>
          </>
        )}

        {challenge?.state === ChallengeState.DETERMINED && (
          <>
            <InfoCallout surface="white">
              <CalloutText>
                This challenge has been determined: {challenge.upheld ? "upheld" : "dismissed"},
                {challenge.inBreach ? " covenant in breach." : " covenant not in breach."}
              </CalloutText>
            </InfoCallout>
            <PopupGhostButton onClick={handleClose} fullWidth>
              Close
            </PopupGhostButton>
          </>
        )}

        {/* Seated but the panel's window has lapsed: the seats may be redrawn once. Signatures
            already collected still count until the redraw actually lands, so the verdict controls
            stay usable underneath. */}
        {canRedraw && (
          <>
            <InfoCallout surface="white" tone="warning">
              <CalloutText>
                The panel&apos;s signing window closed {panelCloses?.toLocaleString()} without a determination. It may
                be redrawn once; a backend job normally does this.
              </CalloutText>
            </InfoCallout>
            {seatError && <ErrorNote>{seatError}</ErrorNote>}
            {seating ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
                Redrawing the panel…
              </PopupProcessingButton>
            ) : (
              <PopupGhostButton onClick={() => drawPanel(true)} disabled={verdict.isPending} fullWidth>
                Redraw the panel — pays gas
              </PopupGhostButton>
            )}
          </>
        )}

        {/* Seated: pick both booleans, then collect signatures. Changing either clears them. */}
        {seated && (
          <>
            <SectionLabel>Was the verification unsupported?</SectionLabel>
            <SegmentedChoice
              options={UPHELD_OPTIONS}
              value={upheldChoice}
              onChange={setUpheldChoice}
              disabled={verdict.isPending}
            />
            <SectionLabel>Is the covenant in breach?</SectionLabel>
            <SegmentedChoice
              options={BREACH_OPTIONS}
              value={breachChoice}
              onChange={setBreachChoice}
              disabled={verdict.isPending}
            />

            {verdictChosen && (
              <InfoCallout surface="white" tone={upheld || inBreach ? "warning" : "info"}>
                <CalloutText>
                  {upheld
                    ? challenge?.kind === ChallengeKind.FIELD
                      ? "Upheld field finding: the verifier and the standing attestor are flagged, the attest slot " +
                        "is forfeited and the seat moves to the challenger, who re-verifies the land."
                      : "Upheld evidence finding: the verifier and the standing attestor are flagged and releases " +
                        "halt until a better record is filed. No seat moves."
                    : "Dismissed: the verification stands and the challenger is flagged."}
                  {stage === "deed" &&
                    (upheld ? " The settlement stays held until re-verification." : " The settlement is released now.")}
                  {inBreach
                    ? " The covenant's TREE rewards freeze until it is re-verified; back-pay resumes on cure."
                    : ""}
                </CalloutText>
              </InfoCallout>
            )}

            {verdictChosen && fallback && (
              <InfoCallout surface="white" tone="warning">
                <CalloutText>
                  The signature service is unreachable, so signatures are collected here instead: each seat signs and
                  sends theirs to whoever submits, who pays the gas.
                </CalloutText>
              </InfoCallout>
            )}

            {verdictChosen ? (
              fallback ? (
                <PanelSignatureCollector verdict={verdict} submitLabel="Record determination" onCancel={handleClose} />
              ) : (
                <VerdictSignatureTally
                  lane={serverLane}
                  upheld={upheld}
                  inBreach={inBreach}
                  viewerAddress={viewerAddress}
                  onCancel={handleClose}
                />
              )
            ) : (
              <PopupGhostButton onClick={handleClose} fullWidth>
                Cancel
              </PopupGhostButton>
            )}
          </>
        )}

        {!challenge && !loading && (
          <PopupGhostButton onClick={handleClose} fullWidth>
            Close
          </PopupGhostButton>
        )}

        <Footnote>
          {challenge?.panelPath === false
            ? "One drawn checker decides this challenge by signing the verdict."
            : "Two of the three drawn seats must sign the same verdict."}
          {fallback
            ? " Whoever submits it pays the gas."
            : " Signing is free: the server records the determination on chain once enough seats agree."}
        </Footnote>
      </DialogSheet>
    </PopupDialog>
  );
};

export default RecordOutcomeDialog;
